<template>
    <div class="select-box"
         :class="{
            'opened': !!opened,
            'disabled': !!isDisabled,
         }"
         :id="id"
         @click="onSelectBoxEvent"
    >
        <div class="select-box--toggle">
            <div v-if="!multiple">
                {{ label }}
            </div>
            <div v-else
                 class="select-box--tag-wrapper"
            >
                <span v-if="!componentValue.length">
                    {{ placeholder }}
                </span>
                <template v-else>
                    <span v-for="(item, index) in componentValue"
                          :key="index"
                          class="select-box--tag"
                    >
                        {{ item[defaultLabelName] }}
                        <a href="#"
                           @click="onTagCloseEvent($event, item)"
                        >
                            <i class="uil uil-times"></i>
                        </a>
                    </span>
                </template>
            </div>
            <i v-if="!!hasItems"
               class="cil"
               :class="{
                   'uil-angle-down': !opened,
                   'uil-angle-up': !!opened
               }"
            ></i>
        </div>
        <div class="select-box--body"
             :class="[
                 position,
                 getDisplayClass
             ]"
             :style="[visibility]"
             :ref="`${id}--body`"
        >
            <input v-if="!!searchable"
                   v-model="searchValue"
                   :placeholder="searchPlaceholder"
                   autofocus
                   @click.stop
                   @focus.stop
            />
            <ul>
                <li v-if="!!defaultItem && !searchValue && ! multiple">
                    <a href="#"
                       :class="{
                            'active': componentValue === false
                       }"
                       @click.prevent="onSelectItemEvent"
                    >
                        {{ placeholder }}
                    </a>
                </li>
                <template v-if="!!getOptions.length">
                    <li v-for="(option, index) in getOptions"
                        :key="index"
                    >
                        <a href="#"
                           :class="{
                                'active': componentValue === getOptionValue(option)
                           }"
                           @click.prevent="onSelectItemEvent($event, option)"
                        >
                            <input v-if="!!multiple"
                                   type="checkbox"
                                   :checked="value.includes(getOptionValue(option))"
                            />
                            {{ getOptionLabel(option) }}
                        </a>
                    </li>
                </template>
                <div v-else>
                    {{ $t('exceptions.no_options_found') }}
                </div>
            </ul>
        </div>
    </div>
</template>

<script>
import Translate from '../../../../Src/Modules/Localization/Translate';
import { isObject, debounce } from 'lodash';
import RemoveDuplicates from '../../../../Src/Utils/Array/RemoveDuplicates';

export default {
    name: 'SelectBox',
    emits: [
        'update:modelValue',
        'update',
    ],
    props: {
        value: {
            type: [Number, String, Array, Boolean]
        },
        modelValue: {
            type: [Number, String, Array, Boolean]
        },
        input: {},
        options: {
            type: Array,
            default: () => ([])
        },
        name: {
            type: String,
            default: 'select-box'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: Translate('placeholders.select_option')
        },
        defaultItem: {
            type: Boolean,
            default: true
        },
        defaultLabelName: {
            type: String,
            default: 'label'
        },
        defaultValueName: {
            type: String,
            default: 'value'
        },
        searchable: {
            type: Boolean,
            default: false
        },
        searchPlaceholder: {
            type: String,
            default: Translate('placeholders.search_value')
        },
        searchValueName: {
            type: String,
            default: 'value'
        },
        id: {
            type: String,
            default: 'selectBox'
        },
        multiple: {
            type: Boolean,
            default: false
        },
        casting: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            opened: false,
            searchValue: null,
            position: null,
            elementHeight: 0,
            resizeObserver: null,
            visibility: {
                visibility: 'hidden'
            },
        };
    },
    computed: {
        componentValue() {
            const value = !!this.value
                ? this.value
                : this.modelValue;

            const newValue = this.resolveCasting(this.casting, value);

            return !this.multiple
                ? newValue
                : this.getOptions.filter(item => newValue.includes(item[this.defaultValueName]))
        },
        label() {
            const value = this.options.filter(option => {
                const inlineValue = this.getOptionValue(option);

                return this.componentValue === inlineValue;
            })[0];

            return this.componentValue !== false
                ? this.getOptionLabel(value)
                : this.placeholder;
        },
        hasItems() {
            return !!this.options.length;
        },
        isDisabled() {
            return !this.hasItems || !!this.disabled;
        },
        getOptions() {
            return !this.searchValue
                ? this.options
                : this.options.filter(option => {
                    const inlineValue = this.getOptionLabel(option).toLowerCase();

                    return inlineValue.indexOf(this.searchValue.toLowerCase()) !== -1;
                });
        },
        getDisplayClass() {
            if (!!this.visibility) {
                return null;
            }

            return !!this.opened
                ? 'opened'
                : 'hidden';
        },
    },
    methods: {
        getOptionLabel(option) {
            return isObject(option)
                ? option[this.defaultLabelName]
                : option;
        },
        getOptionValue(option) {
            return isObject(option)
                ? option[this.defaultValueName]
                : this.resolveCasting(this.casting, option);
        },
        onSelectBoxEvent(event) {
            event.preventDefault();
            event.stopPropagation();

            if (!this.hasItems) {
                return;
            }

            this.opened = !this.opened;

            if (!this.opened) {
                this.searchValue = null;
            }
        },
        onSelectItemEvent(event, value = null) {
            if (!!this.multiple) {
                event.preventDefault();
                event.stopPropagation();
            }

            let tmpValue = !!value
                ? this.getOptionValue(value)
                : null;

            if (!!this.multiple) {
                const currentValues = [
                    ...this.value,
                    tmpValue
                ];

                tmpValue = RemoveDuplicates(currentValues);
            }

            this.updateComponentValue(tmpValue);
        },
        onTagCloseEvent(event, tagItem) {
            event.preventDefault();
            event.stopPropagation();

            const items = this.componentValue.filter(item => item[this.defaultValueName] !== tagItem[this.defaultValueName])
                .map(item => item[this.defaultValueName]);

            this.updateComponentValue(items);
        },
        updateComponentValue(value) {
            this.$emit('update:modelValue', value);
            this.$emit('update', this.name, value);
        },
        onResize() {
            const appElement = document.getElementById('app');
            const appElementHeight = appElement.clientHeight;

            const element = this.$refs[`${this.id}--body`];
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top;
            this.elementHeight = element.clientHeight;

            if (!!this.elementHeight && !!elementTop) {
                const tempHeight = elementTop + this.elementHeight;
                this.position = !!elementTop && tempHeight > appElementHeight
                    ? 'top'
                    : 'bottom';

                this.visibility = null;
                this.destroyResizeObserver();
            }
        },
        destroyResizeObserver() {
            if (!!this.resizeObserver) {
                this.resizeObserver.unobserve(this.$refs[`${this.id}--body`]);

                this.resizeObserver = null;
            }
        },
        resolveCasting(casting, value) {
            switch (casting) {
                case 'number':
                    return Number(value);
                default:
                    return value;
            }
        },
    },
    created() {
        document.addEventListener('click', () => {
            this.opened = false;
        });
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$refs[`${this.id}--body`]);
        this.onResize();
    },
    beforeUnmount() {
        this.destroyResizeObserver();
    },
}
</script>